import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { EmailField, PhoneField } from '../../components/contact-fields.js';
import { Badge, Button, Callout, Loading, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { customerName, quickAddDefaults } from './customer.js';
import { DuplicateNotice, useDuplicates } from './duplicates.js';
import './customers.css';

/**
 * Putting a customer on the sale (SCOPE §8.1, §9.1).
 *
 * Budget: **≤3 interactions to attach** — press, type, pick — and **≤5
 * including a quick add**. Everything here is shaped by those two numbers.
 * Attaching a customer is optional on every sale, and an optional step that
 * costs eight interactions is a step that stops happening, which quietly
 * empties the purchase history the whole of §9.1 is built on.
 */

export interface AttachedCustomer {
  readonly id: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly companyName: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly taxExempt: boolean;
  /** Money the shop already owes them. Summed from the ledger by the server. */
  readonly storeCreditCents: number;
}

/**
 * Whatever the server says the sale is now.
 *
 * Attaching a customer can change the totals — a tax-exempt customer zeroes the
 * tax — so the mutation returns the whole sale and the register re-renders from
 * it, rather than patching a customer name onto figures it already had. Same
 * rule as everywhere else: money math lives on the server.
 */
type SaleView = Awaited<ReturnType<typeof api.register.attachCustomer.mutate>>;

export function CustomerOnSale({
  saleId,
  customer,
  readOnly = false,
  onAttached,
}: {
  saleId: string;
  customer: AttachedCustomer | null;
  /** A refund inherits the original sale's customer; changing it means nothing. */
  readOnly?: boolean;
  onAttached: (view: SaleView) => void;
}) {
  const [open, setOpen] = useState(false);

  if (readOnly) {
    // §2.2-13: no button, because there is no action behind it. The name is
    // still shown — it is context the cashier needs on a refund.
    return customer === null ? null : (
      <span className="customer-chip">
        {customerName(customer)}
        {customer.taxExempt ? <Badge tone="info">Tax exempt</Badge> : null}
      </span>
    );
  }

  const name = customer === null ? 'Add customer' : customerName(customer);

  return (
    <>
      {/*
        Capped width, with the full name on `title` (decision R3).
        Uncapped, attaching "Christopher Vandenberg-Whitmore" widens this button
        and slides *Hold* and *More* sideways mid-sale — which is precisely what
        §2.2-4's "after a week, hands operate the register without eyes" cannot
        survive. The name is the display and the control at once (decision 55),
        so it has to be readable *and* stay put; ellipsis buys both.
      */}
      <Button variant="ghost" onClick={() => setOpen(true)}>
        <span className="customer-button__name" title={customer === null ? undefined : name}>
          {name}
        </span>
        {customer?.taxExempt === true ? <Badge tone="info">Tax exempt</Badge> : null}
      </Button>

      {open ? (
        <CustomerPicker
          saleId={saleId}
          attached={customer}
          onClose={() => setOpen(false)}
          onAttached={(view) => {
            onAttached(view);
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * What the counter needs to know about the person, once one is attached
 * (revision item 4a).
 *
 * Phone and email were already on the wire — `getSaleView` has selected them
 * since the register was built — and the screen threw them away, so staff were
 * asked "what is their number?" while the answer was already loaded. Store
 * credit is the addition, and it is the one that changes behaviour: **credit
 * nobody can see is credit nobody offers**, and it then sits on the account
 * until the customer thinks to ask, which is the same failure as a gift card
 * the shop forgot it had sold.
 *
 * Deliberately a *line under the control row* rather than more text on the
 * button. It has to grow when a customer is attached without moving *Hold* and
 * *More* sideways (decision R3), and the header is where a customer's details
 * belong (§2.2-4, context at the top).
 *
 * Credit is the loud part and the contacts are quiet, because they answer
 * different questions: the credit is money that changes what you offer, and the
 * phone number is looked up when somebody asks for it. It never out-sizes the
 * grand total, which stays the one focal point on the screen (§2.1).
 */
export function CustomerDetail({ customer }: { customer: AttachedCustomer | null }) {
  if (customer === null) return null;

  const hasCredit = customer.storeCreditCents > 0;
  const hasContact = customer.phone !== null || customer.email !== null;
  if (!hasCredit && !hasContact) return null;

  return (
    <div className="customer-detail">
      {hasCredit ? (
        // Labelled rather than a bare figure: §2.2-10 asks every derived number
        // to say what it is, and "$40.00" beside a name reads as what they owe.
        <span className="customer-detail__credit">
          <span className="label-micro">Store credit</span>
          <span className="customer-detail__amount money">{money(customer.storeCreditCents)}</span>
        </span>
      ) : null}
      {hasContact ? (
        <span className="customer-detail__contacts">
          {customer.phone !== null ? <span>{customer.phone}</span> : null}
          {customer.email !== null ? <span>{customer.email}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CustomerPicker({
  saleId,
  attached,
  title,
  skipLabel,
  onClose,
  onAttached,
}: {
  saleId: string;
  attached: AttachedCustomer | null;
  /** Overridden when the picker is a prompt rather than a chosen action. */
  title?: string;
  /** Names the way past.  when the operator opened this themselves. */
  skipLabel?: string;
  onClose: () => void;
  onAttached: (view: SaleView) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 120);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['customers', 'search', debounced],
    queryFn: () => api.customers.search.query({ term: debounced }),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });

  const attach = useMutation({
    mutationFn: (customerId: string | null) =>
      api.register.attachCustomer.mutate({ saleId, customerId }),
    onSuccess: (view, customerId) => {
      recordBreadcrumb(
        'register',
        customerId === null ? 'removed the customer' : 'attached a customer',
      );
      onAttached(view);
    },
  });

  const create = useMutation({
    mutationFn: async (draft: {
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      confirmDuplicate?: boolean;
    }) => {
      const created = await api.customers.create.mutate(draft);
      return api.register.attachCustomer.mutate({ saleId, customerId: created.id });
    },
    onSuccess: (view) => {
      recordBreadcrumb('register', 'added a customer mid-sale');
      onAttached(view);
    },
  });

  if (adding) {
    return (
      <QuickAdd
        term={term}
        busy={create.isPending}
        error={create.isError ? errorMessage(create.error) : null}
        onBack={() => setAdding(false)}
        onCreate={(draft) => create.mutate(draft)}
        onUseExisting={(customerId) => attach.mutate(customerId)}
      />
    );
  }

  const found = results.data ?? [];

  return (
    <Modal
      open
      title={title ?? (attached === null ? 'Who is this for?' : 'Customer on this sale')}
      description="Search by name, phone or email."
      onClose={onClose}
      secondaryAction={
        <>
          <Button variant="ghost" onClick={onClose}>
            {skipLabel ?? 'Close'}
          </Button>
          {/*
            Always offered, never only-when-nothing-matched. Two customers do
            share a name, and hiding the escape hatch behind an empty result
            list is what makes staff attach the sale to the wrong person.
          */}
          <Button variant="secondary" onClick={() => setAdding(true)}>
            New customer
          </Button>
        </>
      }
      primaryAction={
        attached === null ? undefined : (
          // Detaching is the only destructive thing here and it is reversible,
          // so it does not need a confirm — but it does need to exist (§2.2-11).
          <Button variant="danger" busy={attach.isPending} onClick={() => attach.mutate(null)}>
            Remove from sale
          </Button>
        )
      }
    >
      <input
        className="input"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        placeholder="Solberg · 555 0137 · name@example.com"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && found[0] !== undefined) {
            event.preventDefault();
            attach.mutate(found[0].id);
          }
        }}
      />

      {debounced === '' ? (
        <p className="text-secondary">
          {attached === null
            ? 'Start typing. Nothing is attached to this sale yet.'
            : `${customerName(attached)} is on this sale.`}
        </p>
      ) : results.isPending ? (
        <Loading label="Searching" />
      ) : found.length === 0 ? (
        <p className="text-secondary">
          Nobody matches “{debounced}”. <strong>New customer</strong> starts one from what you
          typed.
        </p>
      ) : (
        <ul className="customer-results">
          {found.map((person, index) => (
            <li key={person.id}>
              <button
                type="button"
                className="customer-result"
                disabled={attach.isPending}
                onClick={() => attach.mutate(person.id)}
              >
                <span className="customer-result__name">
                  {customerName(person)}
                  {person.id === attached?.id ? <Badge tone="success">On this sale</Badge> : null}
                  {person.taxExempt ? <Badge tone="info">Tax exempt</Badge> : null}
                  {person.storeCreditCents > 0 ? (
                    <Badge tone="warning">{money(person.storeCreditCents)} credit</Badge>
                  ) : null}
                </span>
                <span className="customer-result__meta">
                  {[person.phone, person.email].filter((part) => part != null).join(' · ')}
                </span>
                <span className="customer-result__seen">
                  {person.lastVisitAt === null
                    ? 'No purchases yet'
                    : `Last in ${new Date(person.lastVisitAt).toLocaleDateString()}`}
                </span>
                {index === 0 ? <kbd className="search__enter">Enter</kbd> : null}
              </button>
            </li>
          ))}
        </ul>
      )}

      {attach.isError ? <Callout tone="danger">{errorMessage(attach.error)}</Callout> : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Four fields, none of them required individually.
 *
 * Hick's law (§2.2-2) applied to a form: address, birthday, marketing consent
 * and tags all exist on the profile, and none of them belong in front of
 * someone with a queue. The full record is edited on the Customers screen.
 */
function QuickAdd({
  term,
  busy,
  error,
  onBack,
  onCreate,
  onUseExisting,
}: {
  term: string;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onCreate: (draft: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    confirmDuplicate?: boolean;
  }) => void;
  /** Attach a customer who already has this email or phone instead (FB-1104). */
  onUseExisting: (customerId: string) => void;
}) {
  const [draft, setDraft] = useState(() => quickAddDefaults(term));
  const { duplicates, sharesEmail } = useDuplicates(draft.email, draft.phone);
  const set = (field: keyof typeof draft) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const identifiable = Object.values(draft).some((value) => value.trim() !== '');
  const submit = () => {
    if (identifiable) onCreate({ ...draft, ...(sharesEmail ? { confirmDuplicate: true } : {}) });
  };

  return (
    <Modal
      open
      title="New customer"
      description="Enough to find them again. The rest can wait."
      onClose={onBack}
      secondaryAction={
        <Button variant="ghost" onClick={onBack}>
          Back to search
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={busy}
          disabledReason={identifiable ? undefined : 'Enter a name, phone or email'}
          onClick={submit}
        >
          {sharesEmail ? 'Someone else — add anyway' : 'Add and attach'}
        </Button>
      }
    >
      <div className="quick-add">
        <QuickField
          label="First name"
          value={draft.firstName}
          onChange={set('firstName')}
          autoFocus={draft.firstName === ''}
          onSubmit={submit}
        />
        <QuickField
          label="Last name"
          value={draft.lastName}
          onChange={set('lastName')}
          onSubmit={submit}
        />
        {/*
          The shared fields, not a local copy. This form and the one on the
          Customers screen do the same job, and having two of them is how smart
          phone formatting shipped to one screen and not to the one the counter
          uses two hundred times a day.
        */}
        <PhoneField value={draft.phone} onChange={set('phone')} onSubmit={submit} />
        <EmailField value={draft.email} onChange={set('email')} onSubmit={submit} />
      </div>

      <DuplicateNotice duplicates={duplicates} useLabel="Attach them" onUse={onUseExisting} />

      {error !== null ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}

function QuickField({
  label,
  value,
  onChange,
  onSubmit,
  inputMode,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  inputMode?: 'tel' | 'email';
  autoFocus?: boolean;
}) {
  const id = `quick-add-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        value={value}
        autoComplete="off"
        autoFocus={autoFocus}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
    </div>
  );
}

/**
 * "Ask for a customer" on the way to payment (SCOPE §9.1), when the store has
 * that setting on.
 *
 * The same picker the Add-customer button opens, framed as a question with an
 * explicit way past it. **Skip is a real button, not a close box** — a prompt
 * you can only escape by dismissing reads as an error, and staff who feel
 * trapped by it start typing a house account instead, which produces a mailing
 * list of one person who bought everything.
 *
 * `onResolved(null)` means skipped; a sale view means they attached someone.
 * Either way the caller carries straight on to tender.
 */
export function CustomerPrompt({
  saleId,
  onResolved,
}: {
  saleId: string;
  onResolved: (view: SaleView | null) => void;
}) {
  return (
    <CustomerPicker
      saleId={saleId}
      attached={null}
      title="Who is this for?"
      skipLabel="No customer"
      onClose={() => onResolved(null)}
      onAttached={(view) => onResolved(view)}
    />
  );
}
